import { useEffect } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Share2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { loadFonts, fullName } from "@/lib/cardUtils";
import { makeTheme, MONO, layoutOf } from "@/components/card/theme";
import { Block } from "@/components/card/CardSections";
import { LegalLinks } from "@/components/legal/LegalLayout";

const Avatar = ({ t, src, size, extra }) =>
  src ? (
    <img src={fileUrl(src)} alt="" data-testid="public-avatar" className="object-cover" style={{ width: size, height: size, borderRadius: t.avatarR, ...extra }} />
  ) : null;

const Logo = ({ src, className }) => (src ? <img src={fileUrl(src)} alt="Logo" data-testid="public-logo" className={`object-contain ${className}`} /> : null);

const MinimalHeader = ({ t, i }) => (
  <header className="text-center">
    {i.cover_url && <img src={fileUrl(i.cover_url)} alt="" className="mb-0 h-40 w-full object-cover" style={{ borderRadius: t.r }} />}
    <div className={i.cover_url ? "-mt-12 flex justify-center" : "flex justify-center pt-6"}>
      <Avatar t={t} src={i.avatar_url} size={104} extra={{ border: `4px solid ${t.c.background}` }} />
    </div>
    <Logo src={i.logo_url} className="mx-auto mt-4 h-8 max-w-[140px]" />
    <h1 className="mt-5 text-[30px] leading-tight" style={t.heading} data-testid="public-name">{fullName(i) || i.company}</h1>
    {i.role && <p className="mt-2 text-[11px] uppercase tracking-[0.2em]" style={{ color: t.c.text }}>{i.role}</p>}
    {i.company && fullName(i) && <p className="mt-1 text-sm" style={{ color: t.c.title }}>{i.company}</p>}
    {i.bio && <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed">{i.bio}</p>}
  </header>
);

const CorporateHeader = ({ t, i }) => (
  <header>
    <div className="-mx-5 -mt-6 h-44 overflow-hidden" style={{ background: t.c.button }}>
      {i.cover_url && <img src={fileUrl(i.cover_url)} alt="" className="h-full w-full object-cover" />}
    </div>
    <div className="relative -mt-16 p-5" style={t.surface}>
      <div className="flex items-start justify-between">
        <div className="-mt-14"><Avatar t={t} src={i.avatar_url} size={92} extra={{ border: `4px solid ${t.c.surface}` }} /></div>
        <Logo src={i.logo_url} className="h-9 max-w-[120px]" />
      </div>
      <h1 className="mt-3 text-[26px] font-semibold leading-tight tracking-tight" style={t.heading} data-testid="public-name">{fullName(i) || i.company}</h1>
      {i.role && <p className="mt-1 text-sm font-medium" style={{ color: t.c.accent }}>{i.role}</p>}
      {i.company && fullName(i) && <p className="text-sm" style={{ color: t.c.text }}>{i.company}</p>}
      {i.bio && <p className="mt-3 text-[15px] leading-relaxed">{i.bio}</p>}
    </div>
  </header>
);

const CreativeHeader = ({ t, i }) => (
  <header>
    <div className="flex items-center justify-between pt-2">
      {i.logo_url ? <Logo src={i.logo_url} className="h-8 max-w-[120px]" /> : <span className="text-xs uppercase tracking-[0.2em]" style={{ color: t.c.accent }}>{i.company}</span>}
    </div>
    {i.cover_url && <img src={fileUrl(i.cover_url)} alt="" className="mt-5 h-44 w-full object-cover" style={{ borderRadius: t.r }} />}
    <div className={`flex items-end gap-4 ${i.cover_url ? "-mt-14 pl-4" : "mt-8"}`}>
      <Avatar t={t} src={i.avatar_url} size={124} extra={{ boxShadow: `8px 8px 0 ${t.c.accent}`, border: `3px solid ${t.c.background}` }} />
    </div>
    <h1 className="mt-7 text-[46px] leading-[0.95]" style={t.heading} data-testid="public-name">{fullName(i) || i.company}</h1>
    {i.role && <p className="mt-3 text-lg italic" style={{ ...t.heading, color: t.c.accent }}>{i.role}</p>}
    {i.company && fullName(i) && i.logo_url && <p className="text-sm">{i.company}</p>}
    {i.bio && <p className="mt-4 text-[15px] leading-relaxed">{i.bio}</p>}
  </header>
);

const FutureHeader = ({ t, i }) => (
  <header className="relative text-center">
    <div className="dcs-aurora pointer-events-none absolute -top-24 left-1/2 h-80 w-80 rounded-full opacity-50 blur-3xl"
      style={{ background: `radial-gradient(circle at 35% 35%, ${t.c.accent}, transparent 62%), radial-gradient(circle at 70% 70%, ${t.c.title}40, transparent 60%)` }} />
    <div className="relative flex items-center justify-between text-[10px] uppercase tracking-[0.3em]" style={{ fontFamily: MONO, color: t.c.text }}>
      <span className="flex items-center gap-2"><span className="dcs-pulse h-1.5 w-1.5 rounded-full" style={{ background: t.c.accent, boxShadow: `0 0 10px ${t.c.accent}` }} />Digital ID</span>
      {i.logo_url ? <Logo src={i.logo_url} className="h-6 max-w-[90px]" /> : <span>{i.company}</span>}
    </div>
    {i.cover_url && <img src={fileUrl(i.cover_url)} alt="" className="relative mt-6 h-36 w-full object-cover opacity-80" style={{ borderRadius: t.r, WebkitMaskImage: "linear-gradient(to bottom, #000 40%, transparent)", maskImage: "linear-gradient(to bottom, #000 40%, transparent)" }} />}
    {i.avatar_url && <div className={`relative mx-auto grid h-[124px] w-[124px] place-items-center ${i.cover_url ? "-mt-16" : "mt-10"}`}>
      <div className="dcs-ring absolute inset-0" style={{ borderRadius: t.avatarR, background: `conic-gradient(from 0deg, ${t.c.accent}, transparent 35%, ${t.c.accent}55 60%, transparent 80%, ${t.c.accent})` }} />
      <div className="relative" style={{ borderRadius: t.avatarR, padding: 3, background: t.c.background }}>
        <Avatar t={t} src={i.avatar_url} size={112} />
      </div>
    </div>}
    <h1 className={`relative ${i.avatar_url ? "mt-7" : "mt-12"} text-[34px] font-semibold leading-[1.05] tracking-[-0.04em]`} style={t.heading} data-testid="public-name">{fullName(i) || i.company}</h1>
    {i.role && <p className="relative mt-3 text-[10px] uppercase tracking-[0.32em]" style={{ fontFamily: MONO, color: t.c.accent }}>{i.role}</p>}
    {i.company && fullName(i) && <p className="relative mt-1.5 text-sm" style={{ color: t.c.title }}>{i.company}</p>}
    {i.bio && <p className="relative mx-auto mt-5 max-w-sm text-[15px] leading-relaxed">{i.bio}</p>}
  </header>
);

const HEADERS = { minimal: MinimalHeader, corporate: CorporateHeader, creative: CreativeHeader, future: FutureHeader };

export const CardRenderer = ({ card, onSave, onShare, track = () => {}, fixedBg = false }) => {
  const { style, identity } = card;
  useEffect(() => {
    loadFonts([style.font_heading, style.font_body, "Geist Mono"]);
  }, [style.font_heading, style.font_body]);
  const t = makeTheme(style);
  t.icons = card.icons || {};
  const Header = HEADERS[layoutOf(style.template)];
  const blocks = (card.blocks || []).filter((b) => b.visible);
  let idx = 0;

  return (
    <div className="relative min-h-full w-full overflow-hidden" style={{ background: t.c.background, ...t.body }} data-testid="card-renderer">
      {style.background_image && (
        <div className={`pointer-events-none ${fixedBg ? "fixed" : "absolute"} inset-0`} data-testid="card-background-image">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${fileUrl(style.background_image)})`, filter: style.background_blur ? `blur(${style.background_blur}px)` : undefined, transform: style.background_blur ? "scale(1.08)" : undefined }} />
          <div className="absolute inset-0" style={{ background: t.c.background, opacity: (style.background_overlay ?? 40) / 100 }} />
        </div>
      )}
      {t.tpl === "future" && (
        <div className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{ backgroundImage: `linear-gradient(${t.c.border} 1px, transparent 1px), linear-gradient(90deg, ${t.c.border} 1px, transparent 1px)`, backgroundSize: "36px 36px", WebkitMaskImage: "linear-gradient(to bottom, #000, transparent 45%)", maskImage: "linear-gradient(to bottom, #000, transparent 45%)" }} />
      )}
      <div className="relative mx-auto max-w-md px-5 pb-10 pt-6">
        <div className="dcs-in"><Header t={t} i={identity} /></div>
        <div className="dcs-in mt-7 flex gap-2.5" style={{ animationDelay: "120ms" }}>
          <button onClick={onSave} data-testid="public-card-save-contact-button"
            className="dcs-shimmer flex flex-1 items-center justify-center gap-2 px-5 py-3.5 text-[15px] font-semibold transition-transform duration-150 active:scale-[0.98]"
            style={{ ...t.primary, background: t.tpl === "minimal" ? t.c.title : t.c.button, borderColor: t.tpl === "minimal" ? t.c.title : t.c.button, color: t.c.button_text }}>
            <UserPlus className="h-[18px] w-[18px]" strokeWidth={1.8} /> Salva contatto
          </button>
          <button onClick={onShare} aria-label="Condividi" data-testid="public-card-share-button"
            className="grid w-[52px] place-items-center transition-transform duration-150 active:scale-95" style={{ ...t.secondary, background: t.tpl === "future" ? t.secondary.background : "transparent", border: `1px solid ${t.tpl === "future" ? t.c.border : `${t.c.title}33`}` }}>
            <Share2 className="h-[18px] w-[18px]" strokeWidth={1.6} />
          </button>
        </div>
        <div className={`mt-9 ${t.boxed ? "space-y-4" : "space-y-9"}`}>
          {blocks.map((b, n) => (
            <div key={b.id} className="dcs-in" style={{ animationDelay: `${200 + n * 70}ms` }}>
              <Block t={t} block={b} card={card} track={track} index={b.type === "divider" ? idx : idx++} />
            </div>
          ))}
        </div>
        <footer className="mt-14 text-center text-[11px]" style={{ color: t.c.text }}>
          {card.settings?.show_footer && card.brand_name && <div data-testid="footer-branding">Digital card by <span className="font-semibold">{card.brand_name}</span></div>}
          <LegalLinks prefix="public" className="mt-1.5 opacity-80" />
        </footer>
      </div>
    </div>
  );
};
